// DriverDashboard.tsx - Fixed to handle auth state properly
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Leaf, LogOut, Bell, Users } from 'lucide-react';
import { UserProfile, DriverTabType, RideRequest, RideWithRequests, Conversation } from '@/types';
import { useCallback } from 'react';
import { CreateRidePayload } from '@/lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ridesApi, riderRideRequestsApi, messagesApi, feedApi, notificationsApi, profileApi, getApiErrorMessage, RideRequestAction, RiderRideRequestAction } from '@/lib/api';
import { useRealtime } from '@/hooks/useRealtime';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import BottomNav from '@/components/BottomNav';
import RideCard from '@/components/RideCard';
import PostRideForm from '@/components/PostRideForm';
import ChatPanel from '@/components/ChatPanel';
import ProfileCard from '@/components/ProfileCard';
import SafetyFooter from '@/components/SafetyFooter';
import ConversationList from '@/components/ConversationList';
import FeedsTab from '@/components/FeedsTab';
import PostModal from '@/components/PostModal';
import NotificationsPanel from '@/components/NotificationsPanel';
import RideRequestsPanel from '@/components/RideRequestsPanel';
import ProfileEditModal from '@/components/ProfileEditModal';
import { RiderRideRequestCard } from '@/components/RiderRideRequestCard';

interface DriverDashboardProps {
  currentUser: UserProfile;
  onLogout: () => void;
  onUserUpdate?: (updatedUser: UserProfile) => void;
}

const DRIVER_TABS: readonly DriverTabType[] = ['home', 'post', 'feeds', 'messages', 'profile'];

const DriverDashboard = ({ currentUser, onLogout, onUserUpdate }: DriverDashboardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isPostingRide, setIsPostingRide] = useState(false);
  
  // Check for openChat query param to open chat directly
  const searchParams = new URLSearchParams(location.search);
  const openChatId = searchParams.get('openChat');
  
  // DEBUG: Log when DriverDashboard renders
  console.log('[DEBUG] DriverDashboard rendering, currentUser:', currentUser);
  
  // Ensure we stay on driver dashboard - protect against unexpected navigation
  useEffect(() => {
    // If somehow we're on wrong page, redirect back to driver
    if (location.pathname !== '/driver') {
      console.log('[DEBUG] DriverDashboard: Unexpected pathname, staying on /driver');
      // Don't navigate away - we're already here
    }
  }, [location.pathname]);
  
  // Protect against navigation away while posting
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isPostingRide) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isPostingRide]);
  
  const [activeTab, setActiveTab] = useState<DriverTabType>('home');
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [selectedRideForRequests, setSelectedRideForRequests] = useState<string | null>(null);
  const [statusTab, setStatusTab] = useState<'riderRequests' | 'pending' | 'ongoing' | 'completed'>('riderRequests');
  const [respondingRequestId, setRespondingRequestId] = useState<string | null>(null);
  
  const queryClient = useQueryClient();
  
  // Call useRealtime at TOP LEVEL (not inside useEffect) - this is a React hook!
  // It will only activate when currentUser.id exists
  useRealtime(currentUser?.id);

  // Only fetch data when user is available
  const { data: myRides = [], isLoading: ridesLoading } = useQuery({
    queryKey: ['myRides', currentUser?.id],
    queryFn: ridesApi.getMyRides,
    enabled: !!currentUser?.id,
    refetchInterval: 10000,
  });

  const { data: riderRideRequests = [], isLoading: riderRideRequestsLoading } = useQuery({
    queryKey: ['riderRideRequests', 'available', currentUser?.id],
    queryFn: riderRideRequestsApi.getAvailable,
    enabled: !!currentUser?.id,
    refetchInterval: 10000,
  });

  const { data: conversations = [] } = useQuery({
    queryKey: ['conversations', currentUser?.id],
    queryFn: messagesApi.getConversations,
    enabled: !!currentUser?.id,
    refetchInterval: 5000, // Poll every 5 seconds for new conversations
  });

  const { data: posts = [] } = useQuery({
    queryKey: ['posts'],
    queryFn: feedApi.getPosts,
    enabled: activeTab === 'feeds',
  });

  const createRideMutation = useMutation({
    mutationFn: async (data: CreateRidePayload) => {
      setIsPostingRide(true);
      const newRide = await ridesApi.createRide(data);
      return newRide;
    },
    onSuccess: (newRide) => {
      console.log('[DEBUG] Ride posted successfully:', newRide.id, newRide);
      // Optimistic update: immediately add to myRides cache
      queryClient.setQueryData(['myRides', currentUser?.id], (old: any[] = []) => 
        [newRide, ...old.filter((ride) => ride.id !== newRide.id)]
      );
      toast.success('Ride posted successfully!');
      queryClient.invalidateQueries({ queryKey: ['myRides', currentUser?.id] });
      queryClient.invalidateQueries({ queryKey: ['availableRides'] });
      setStatusTab('pending');
      setActiveTab('home');
      if (location.pathname !== '/driver') {
        navigate('/driver', { replace: true });
      }
    },
    onError: (error: any) => {
      console.error('[DEBUG] Failed to post ride:', error);
      toast.error(getApiErrorMessage(error, 'Failed to post ride'));
    },
    onSettled: () => {
      setIsPostingRide(false);
    },
  });

  // Feed post mutation - added for creating posts
  const createPostMutation = useMutation({
    mutationFn: (data: any) => feedApi.createPost(data),
    onSuccess: () => {
      toast.success('Post created successfully!');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      setShowPostModal(false);
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to create post'));
    },
  });

  // Delete ride mutation
  const deleteRideMutation = useMutation({
    mutationFn: (rideId: string) => ridesApi.deleteRide(rideId),
    onSuccess: () => {
      toast.success('Ride deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['myRides'] });
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete ride'));
    },
  });

  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: any) => profileApi.updateProfile(data),
    onSuccess: (updatedUser) => {
      // Update localStorage with new user data
      localStorage.setItem('collegeryde_user', JSON.stringify(updatedUser));
      // Force a refetch of the user data to update UI immediately
      queryClient.invalidateQueries({ queryKey: ['currentUserProfile'] });
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      // Call the onUserUpdate callback to update parent state
      onUserUpdate?.(updatedUser);
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to update profile'));
    },
  });

  // Fetch current user profile for fresh data
  const { data: freshUserData } = useQuery({
    queryKey: ['currentUserProfile', currentUser?.id],
    queryFn: () => profileApi.getProfile(currentUser!.id),
    enabled: !!currentUser?.id,
    staleTime: 0, // Always consider stale to ensure fresh data
  });

  // Use fresh user data if available, otherwise fall back to currentUser prop
  // IMPORTANT: We must merge freshUserData with currentUser to ensure avatar and other fields
  // are preserved, since the API response might not include all fields (e.g., avatar)
  const displayUser: UserProfile = freshUserData 
    ? { ...currentUser, ...freshUserData } 
    : currentUser;

  const respondToRequestMutation = useMutation({
    mutationFn: async ({ rideId, requestId, action }: { rideId: string; requestId: string; action: RideRequestAction }) => {
      setRespondingRequestId(requestId);
      return ridesApi.respondToRequest(rideId, requestId, action);
    },
    onSuccess: (_, variables) => {
      const result = variables.action === 'accept' ? 'accepted' : 'declined';
      toast.success(`Request ${result}! Rider has been notified.`);
      queryClient.invalidateQueries({ queryKey: ['myRides'] });
      queryClient.invalidateQueries({ queryKey: ['rideRequests', variables.rideId] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['myRequests'] });
      queryClient.invalidateQueries({ queryKey: ['availableRides'] });
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to respond to request'));
    },
    onSettled: () => {
      setRespondingRequestId(null);
    },
  });

  const respondToRiderRideRequestMutation = useMutation({
    mutationFn: ({ requestId, action }: { requestId: string; action: RiderRideRequestAction }) => {
      setRespondingRequestId(requestId);
      return riderRideRequestsApi.respond(requestId, action);
    },
    onSuccess: (_, variables) => {
      toast.success(
        variables.action === 'accept'
          ? 'Ride request accepted. You can message the rider now.'
          : variables.action === 'cancel'
            ? 'Accepted request removed from Pending.'
            : 'Ride request rejected.'
      );
      queryClient.invalidateQueries({ queryKey: ['riderRideRequests'] });
      queryClient.invalidateQueries({ queryKey: ['myRides'] });
      if (variables.action === 'accept') setStatusTab('pending');
    },
    onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to respond to rider request')),
    onSettled: () => setRespondingRequestId(null),
  });

  const startConversationMutation = useMutation({
    mutationFn: ({ userId, rideId }: { userId: string; rideId?: string }) =>
      messagesApi.startConversation({ userId, rideId }),
    onSuccess: (conversation) => {
      setSelectedConversation(conversation);
      setIsChatOpen(true);
      setActiveTab('messages');
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => {
      toast.error('Failed to start conversation');
    },
  });

  const handlePostRide = async (data: CreateRidePayload) => {
    await createRideMutation.mutateAsync(data);
  };

  const handleRespondToRequest = (rideId: string, requestId: string, action: RideRequestAction) => {
    respondToRequestMutation.mutate({ rideId, requestId, action });
  };

  const handleStartConversation = async (riderId: string, rideId: string) => {
    const existingConversation =
      conversations.find((conversation) =>
        conversation.ride?.id === rideId &&
        conversation.participants.some((participant) => participant.id === riderId)
      ) ||
      conversations.find((conversation) =>
        conversation.participants.some((participant) => participant.id === riderId)
      );

    if (existingConversation) {
      setSelectedConversation(existingConversation);
      setIsChatOpen(true);
      setActiveTab('messages');
      return;
    }

    startConversationMutation.mutate({ userId: riderId, rideId });
  };

  const handleRiderRequestConversation = (riderId: string) => {
    const existingConversation = conversations.find((conversation) =>
      conversation.participants.some((participant) => participant.id === riderId)
    );
    if (existingConversation) {
      setSelectedConversation(existingConversation);
      setIsChatOpen(true);
      setActiveTab('messages');
      return;
    }
    startConversationMutation.mutate({ userId: riderId });
  };

  const handleViewRiderProfile = (riderId: string) => {
    navigate(`/profile/${riderId}`);
  };

  const handleViewProfile = (userId: string) => {
    if (userId) navigate(`/profile/${userId}`);
  };

  const handleTabChange = (tab: DriverTabType) => {
    setActiveTab(tab);
    if (tab !== 'messages') {
      setIsChatOpen(false);
    }
  };

  // Compute trip status with fallback
  const getTripStatus = useCallback((ride: any) => {
    const ts = ride.tripStatusDriver || ride.tripStatus || ride.status;
    if (ts === 'completed' || ts === 'ended') return 'completed';
    if (ts === 'ongoing') return 'ongoing';
    return 'pending';
  }, []);

  // Filter rides by status for tabs
  const filteredPendingRides = myRides.filter((ride: any) => 
    getTripStatus(ride) === 'pending'
  );
  const filteredOngoingRides = myRides.filter((ride: any) => 
    getTripStatus(ride) === 'ongoing'
  );
  const filteredCompletedRides = myRides.filter((ride: any) => 
    getTripStatus(ride) === 'completed'
  );
  const openRiderRideRequests = riderRideRequests.filter((request) => request.status === 'open');
  const acceptedRiderRideRequests = riderRideRequests.filter((request) => request.status === 'accepted');

  const pendingRequestsCount = myRides.reduce((acc: number, ride: any) => {
    const pending = (ride.requests || []).filter((r: any) => r.status === 'pending').length;
    return acc + pending;
  }, 0);

  // Calculate total unread messages from all conversations
  const unreadMessagesCount = conversations.reduce(
    (total, conv) => total + (conv.unreadCount || 0), 0
  );

  // Handle opening chat from URL query param (e.g., from profile page)
  useEffect(() => {
    if (openChatId && conversations.length > 0) {
      const conversation = conversations.find(c => c.id === openChatId);
      if (conversation) {
        setSelectedConversation(conversation);
        setIsChatOpen(true);
        setActiveTab('messages');
        // Clear the URL param
        navigate(location.pathname, { replace: true });
      }
    }
  }, [openChatId, conversations, navigate, location.pathname]);

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return (
          <div className="space-y-5 animate-fade-in">
            {pendingRequestsCount > 0 && (
              <div className="bg-primary/10 border border-primary/20 rounded-2xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary rounded-full">
                    <Users className="w-4 h-4 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">New Ride Requests</p>
                    <p className="text-xs text-muted-foreground">{pendingRequestsCount} pending</p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedRideForRequests('all')}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  View All
                </button>
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg">My Rides</h2>
              <button 
                onClick={() => queryClient.invalidateQueries({ queryKey: ['myRides', currentUser?.id] })}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors p-2 rounded-lg hover:bg-muted"
              >
                ↻ Refresh
              </button>
            </div>
            <div className="space-y-4">
              <Tabs value={statusTab} onValueChange={(value) => setStatusTab(value as typeof statusTab)} className="w-full">
                <TabsList className="grid h-auto w-full grid-cols-4">
                  <TabsTrigger value="riderRequests" className="px-1 text-xs sm:text-sm">Rider requests</TabsTrigger>
                  <TabsTrigger value="pending">Pending</TabsTrigger>
                  <TabsTrigger value="ongoing">Ongoing</TabsTrigger>
                  <TabsTrigger value="completed">Completed</TabsTrigger>
                </TabsList>
                <TabsContent value="riderRequests" className="mt-4 space-y-4">
                  <div>
                    <h3 className="font-display font-bold">Riders looking for a driver</h3>
                    <p className="text-sm text-muted-foreground">Emergency requests are shown first.</p>
                  </div>
                  {riderRideRequestsLoading ? (
                    <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>
                  ) : openRiderRideRequests.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No open rider requests nearby.</div>
                  ) : [...openRiderRideRequests]
                    .sort((a, b) => Number(b.isEmergency) - Number(a.isEmergency))
                    .map((request) => (
                      <RiderRideRequestCard
                        key={request.id}
                        request={request}
                        driverView
                        isResponding={respondingRequestId === request.id}
                        onAccept={() => respondToRiderRideRequestMutation.mutate({ requestId: request.id, action: 'accept' })}
                        onReject={() => respondToRiderRideRequestMutation.mutate({ requestId: request.id, action: 'reject' })}
                        onMessageRider={() => handleRiderRequestConversation(request.rider.id)}
                      />
                    ))}
                </TabsContent>
                <TabsContent value="pending" className="mt-4 space-y-4">
                  {acceptedRiderRideRequests.length > 0 && (
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-display font-bold">Accepted rider requests</h3>
                        <p className="text-sm text-muted-foreground">Requests you accepted and are preparing to fulfill.</p>
                      </div>
                      {acceptedRiderRideRequests.map((request) => (
                        <RiderRideRequestCard
                          key={request.id}
                          request={request}
                          driverView
                          isResponding={respondingRequestId === request.id}
                          onMessageRider={() => handleRiderRequestConversation(request.rider.id)}
                          onDelete={() => respondToRiderRideRequestMutation.mutate({ requestId: request.id, action: 'cancel' })}
                        />
                      ))}
                    </div>
                  )}
                  {ridesLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : filteredPendingRides.length === 0 && acceptedRiderRideRequests.length === 0 ? (
                    <div className="text-center py-12 bg-card rounded-2xl border border-border/50">
                      <p className="text-muted-foreground">No pending rides.</p>
                      <p className="text-sm text-muted-foreground/60 mt-1">Post a ride to get started!</p>
                    </div>
                  ) : (
                    filteredPendingRides.map((ride: RideWithRequests) => (
                      <RideCard 
                        key={ride.id} 
                        ride={ride} 
                        isDriverView={true}
                        statusMode="driver"
                        pendingRequests={ride.requests?.filter((r: RideRequest) => r.status === 'pending').length || 0}
                        onViewRequests={() => setSelectedRideForRequests(ride.id)}
                        onMessageRider={(riderId: string) => handleStartConversation(riderId, ride.id)}
                        onViewRiderProfile={handleViewRiderProfile}
                        onDeleteRide={() => deleteRideMutation.mutate(ride.id)}
                      />
                    ))
                  )}
                </TabsContent>
                <TabsContent value="ongoing" className="mt-4">
                  {filteredOngoingRides.length === 0 ? (
                    <div className="text-center py-12 bg-card rounded-2xl border border-border/50">
                      <p className="text-muted-foreground">No ongoing rides.</p>
                    </div>
                  ) : (
                    filteredOngoingRides.map((ride: RideWithRequests) => (
                      <RideCard 
                        key={ride.id} 
                        ride={ride} 
                        isDriverView={true}
                        statusMode="driver"
                        pendingRequests={ride.requests?.filter((r: RideRequest) => r.status === 'pending').length || 0}
                        onViewRequests={() => setSelectedRideForRequests(ride.id)}
                        onMessageRider={(riderId: string) => handleStartConversation(riderId, ride.id)}
                        onViewRiderProfile={handleViewRiderProfile}
                        onDeleteRide={() => deleteRideMutation.mutate(ride.id)}
                      />
                    ))
                  )}
                </TabsContent>
                <TabsContent value="completed" className="mt-4">
                  {filteredCompletedRides.length === 0 ? (
                    <div className="text-center py-12 bg-card rounded-2xl border border-border/50">
                      <p className="text-muted-foreground">No completed rides.</p>
                    </div>
                  ) : (
                    filteredCompletedRides.map((ride: RideWithRequests) => (
                      <RideCard 
                        key={ride.id} 
                        ride={ride} 
                        isDriverView={true}
                        statusMode="driver"
                        pendingRequests={ride.requests?.filter((r: RideRequest) => r.status === 'pending').length || 0}
                        onViewRequests={() => setSelectedRideForRequests(ride.id)}
                        onMessageRider={(riderId: string) => handleStartConversation(riderId, ride.id)}
                        onViewRiderProfile={handleViewRiderProfile}
                        onDeleteRide={() => deleteRideMutation.mutate(ride.id)}
                      />
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </div>
            <SafetyFooter />
          </div>
        );
      case 'post':
        return (
          <div className="space-y-5 animate-fade-in">
            <div className="bg-card rounded-2xl p-5 shadow-warm border border-border/50">
              <h2 className="font-display font-bold text-lg mb-4">Post a New Ride</h2>
              <PostRideForm 
                onSubmit={handlePostRide} 
                isSubmitting={createRideMutation.isPending} 
              />
            </div>
          </div>
        );
      case 'feeds':
        return <FeedsTab posts={posts} onCreatePost={() => setShowPostModal(true)} currentUser={currentUser} />;
      case 'messages':
        return (
          <ConversationList 
            conversations={conversations} 
            onSelectConversation={(c: Conversation) => { 
              setSelectedConversation(c); 
              setIsChatOpen(true); 
            }}
            onViewProfile={handleViewProfile}
            currentUserId={currentUser?.id}
          />
        );
      case 'profile':
        return (
          <div className="space-y-6 animate-fade-in">
            <ProfileCard user={displayUser} isOwnProfile onEdit={() => setShowProfileEdit(true)} />
            <button 
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border border-destructive/20 text-destructive hover:bg-destructive/5 transition-colors font-medium"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container max-w-lg mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="p-2 gradient-warm rounded-xl shadow-warm">
              <Leaf className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="font-display font-bold text-xl">CollegeRyde</h1>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowNotifications(true)}
              className="p-2 rounded-full hover:bg-muted relative"
            >
              <Bell className="w-5 h-5 text-muted-foreground" />
              {pendingRequestsCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                  {pendingRequestsCount > 9 ? '9+' : pendingRequestsCount}
                </span>
              )}
            </button>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
              DRIVER
            </span>
          </div>
        </div>
      </header>

      <main className="container max-w-lg mx-auto px-4 py-5 pb-24">
        {renderContent()}
      </main>

      <BottomNav<DriverTabType>
        activeTab={activeTab} 
        onTabChange={handleTabChange}
        availableTabs={DRIVER_TABS}
        badgeCounts={{ messages: unreadMessagesCount }}
      />
      
      <ChatPanel
        conversation={selectedConversation}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        currentUser={currentUser}
      />

      <NotificationsPanel 
        isOpen={showNotifications} 
        onClose={() => setShowNotifications(false)}
        currentUserRole="driver"
        onViewRideRequests={(rideId) => {
          setSelectedRideForRequests(rideId);
          setActiveTab('home');
        }}
        onViewRides={() => setActiveTab('home')}
        onViewConversation={(conversationId) => {
          const conversation = conversations.find(c => c.id === conversationId);
          if (conversation) {
            setSelectedConversation(conversation);
            setIsChatOpen(true);
            setActiveTab('messages');
          }
        }}
      />

      <PostModal
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
        user={currentUser}
        onSubmit={async (data) => {
          await createPostMutation.mutateAsync(data);
        }}
      />

      <RideRequestsPanel
        isOpen={!!selectedRideForRequests}
        onClose={() => setSelectedRideForRequests(null)}
        rideId={selectedRideForRequests}
        onRespond={handleRespondToRequest}
        onMessageRider={handleStartConversation}
        respondingRequestId={respondingRequestId}
      />

      <ProfileEditModal
        isOpen={showProfileEdit}
        onClose={() => setShowProfileEdit(false)}
        user={displayUser}
        onSave={async (data) => {
          await updateProfileMutation.mutateAsync(data);
          setShowProfileEdit(false);
        }}
      />
    </div>
  );
};

export default DriverDashboard;
